import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import api, { API_BASE_URL } from '../../services/api';
import { cajaService } from '../../services/cajaService';
import './CajaMovimientosModal.css';

const CajaMovimientosModal = ({ show, onClose, sesionId, filter = 'ALL' }) => {
    const [movimientos, setMovimientos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [metodoPagoFilter, setMetodoPagoFilter] = useState('TODOS');

    useEffect(() => {
        if (show && sesionId) {
            fetchMovimientos();
        }
    }, [show, sesionId]);

    const fetchMovimientos = async () => {
        try {
            setLoading(true);
            const data = await cajaService.obtenerMovimientosSesion(sesionId);
            setMovimientos(data);
        } catch (error) {
            console.error(error);
            Swal.fire('Error', 'No se pudieron cargar los movimientos', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = (comprobanteId) => {
        if (!comprobanteId) return;
        const url = `${API_BASE_URL}/comprobantes/${comprobanteId}/imprimir`;
        window.open(url, '_blank', 'width=800,height=600');
    };

    const handleAnnul = async (mov) => {
        const isPago = mov.tipo === 'INGRESO';
        const title = isPago ? '¿Anular Pago?' : '¿Anular Gasto?';
        const text = isPago
            ? 'Esta acción anulará el comprobante y restaurará la deuda del alumno. ¿Deseas continuar?'
            : 'Esta acción anulará el registro de egreso. ¿Deseas continuar?';

        const result = await Swal.fire({
            title,
            text,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, anular',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                if (isPago && mov.comprobante_id) {
                    await api.delete(`/comprobantes/${mov.comprobante_id}`);
                } else if (mov.egreso_id) {
                    await cajaService.anularMovimientoChica(mov.egreso_id, 'Anulación desde gestión de caja', null);
                } else if (mov.id.startsWith('MOV-')) {
                    const rawId = mov.id.split('-')[1];
                    await cajaService.eliminarMovimiento(rawId);
                }

                Swal.fire('Anulado', 'La operación ha sido anulada exitosamente', 'success');
                fetchMovimientos();
            } catch (error) {
                console.error(error);
                Swal.fire('Error', error.response?.data?.error || 'No se pudo anular la operación', 'error');
            }
        }
    };

    const handleEditMetodoPago = async (mov) => {
        if (!mov.comprobante_id) return;

        // Fetch bank accounts for the selection
        let cuentas = [];
        try {
            const resp = await api.get('/caja/cuentas');
            cuentas = resp.data;
        } catch (error) {
            console.error('Error fetching accounts:', error);
        }

        const { value: formValues } = await Swal.fire({
            title: 'Corregir Método de Pago',
            html: `
                <div class="swal-form-container">
                    <div class="swal-form-group">
                        <label for="swal-input1">Nuevo Método de Pago</label>
                        <select id="swal-input1" class="swal2-input custom-swal-select">
                            <option value="EFECTIVO" ${mov.metodo === 'EFECTIVO' ? 'selected' : ''}>EFECTIVO</option>
                            <option value="YAPE" ${mov.metodo === 'YAPE' ? 'selected' : ''}>YAPE</option>
                            <option value="PLIN" ${mov.metodo === 'PLIN' ? 'selected' : ''}>PLIN</option>
                            <option value="TARJETA" ${mov.metodo === 'TARJETA' ? 'selected' : ''}>TARJETA</option>
                            <option value="TRANSFERENCIA" ${mov.metodo === 'TRANSFERENCIA' ? 'selected' : ''}>TRANSFERENCIA</option>
                            <option value="DEPOSITO" ${mov.metodo === 'DEPOSITO' ? 'selected' : ''}>DEPÓSITO</option>
                        </select>
                    </div>
                    <div id="cuenta-selector" class="swal-form-group" style="display: ${['TRANSFERENCIA', 'DEPOSITO'].includes(mov.metodo) ? 'block' : 'none'}">
                        <label for="swal-input2">Seleccione Cuenta Bancaria</label>
                        <select id="swal-input2" class="swal2-input custom-swal-select">
                            ${cuentas.map(c => `<option value="${c.id}" ${mov.cuenta_bancaria_id === c.id ? 'selected' : ''}>${c.banco} - ${c.numero_cuenta}</option>`).join('')}
                        </select>
                    </div>
                </div>
            `,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Actualizar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#4f46e5',
            cancelButtonColor: '#64748b',
            customClass: {
                popup: 'swal-premium-popup',
                title: 'swal-premium-title',
                confirmButton: 'swal-premium-confirm',
                cancelButton: 'swal-premium-cancel'
            },
            didOpen: () => {
                const methodSelect = document.getElementById('swal-input1');
                const cuentaDiv = document.getElementById('cuenta-selector');
                methodSelect.addEventListener('change', (e) => {
                    if (['TRANSFERENCIA', 'DEPOSITO'].includes(e.target.value)) {
                        cuentaDiv.style.display = 'block';
                    } else {
                        cuentaDiv.style.display = 'none';
                    }
                });
            },
            preConfirm: () => {
                return {
                    nuevo_metodo: document.getElementById('swal-input1').value,
                    cuenta_bancaria_id: document.getElementById('swal-input2').value
                }
            }
        });

        if (formValues) {
            try {
                await api.put(`/caja/pagos/${mov.comprobante_id}/metodo`, formValues);
                Swal.fire({
                    title: 'Actualizado',
                    text: 'El método de pago ha sido corregido',
                    icon: 'success',
                    timer: 1500,
                    showConfirmButton: false
                });
                fetchMovimientos();
            } catch (error) {
                console.error(error);
                Swal.fire('Error', error.response?.data?.error || 'No se pudo actualizar el método de pago', 'error');
            }
        }
    };

    // Filter movements based on the prop and payment method
    const filteredMovimientos = movimientos.filter(mov => {
        // First filter by income/expense
        if (filter === 'INCOME' && mov.es_salida) return false;
        if (filter === 'EXPENSE' && !mov.es_salida) return false;

        // Then filter by payment method
        if (metodoPagoFilter !== 'TODOS') {
            // Extract payment method from description (e.g., "Cobro YAPE - ...")
            const descripcion = mov.descripcion?.toUpperCase() || '';
            const metodo = mov.metodo?.toUpperCase() || '';

            if (metodoPagoFilter === 'EFECTIVO') {
                return descripcion.includes('EFECTIVO') || metodo === 'EFECTIVO';
            } else if (metodoPagoFilter === 'YAPE') {
                return descripcion.includes('YAPE') || metodo === 'YAPE';
            } else if (metodoPagoFilter === 'PLIN') {
                return descripcion.includes('PLIN') || metodo === 'PLIN';
            } else if (metodoPagoFilter === 'TRANSFERENCIA') {
                return descripcion.includes('TRANSFERENCIA') || metodo === 'TRANSFERENCIA';
            } else if (metodoPagoFilter === 'TARJETA') {
                return descripcion.includes('TARJETA') || metodo === 'TARJETA';
            } else if (metodoPagoFilter === 'DEPOSITO') {
                return descripcion.includes('DEPOSITO') || descripcion.includes('DEPÓSITO') || metodo === 'DEPOSITO';
            }
        }
        return true;
    });

    const calculateTotal = () => {
        return filteredMovimientos.reduce((acc, mov) => {
            return acc + (mov.es_salida ? -mov.monto : mov.monto);
        }, 0);
    };

    const getModalTitle = () => {
        if (filter === 'INCOME') return 'Detalle de Ingresos de Caja';
        if (filter === 'EXPENSE') return 'Detalle de Egresos de Caja';
        return 'Detalle de Movimientos de Caja';
    };

    if (!show) return null;

    return (
        <div className="modal-overlay-movimientos">
            <div className="modal-container-movimientos">
                <div className="modal-header-movimientos">
                    <h3>{getModalTitle()}</h3>
                    <div className="header-controls">
                        <div className="filter-metodo-pago">
                            <label htmlFor="metodoPagoFilter">Filtrar por:</label>
                            <select
                                id="metodoPagoFilter"
                                value={metodoPagoFilter}
                                onChange={(e) => setMetodoPagoFilter(e.target.value)}
                                className="form-select form-select-sm"
                            >
                                <option value="TODOS">Todos los métodos</option>
                                <option value="EFECTIVO">💵 Efectivo</option>
                                <option value="YAPE">📱 Yape</option>
                                <option value="PLIN">📱 Plin</option>
                                <option value="TRANSFERENCIA">🏦 Transferencia</option>
                                <option value="TARJETA">💳 Tarjeta</option>
                                <option value="DEPOSITO">🏦 Depósito</option>
                            </select>
                        </div>
                    </div>
                    <button className="btn-close-movimientos" onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className="modal-body-movimientos">
                    {loading ? (
                        <div className="loading-state">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Cargando...</span>
                            </div>
                            <p>Cargando movimientos...</p>
                        </div>
                    ) : filteredMovimientos.length === 0 ? (
                        <div className="empty-state">
                            <i className="flaticon-381-search-2"></i>
                            <p>No hay {filter === 'INCOME' ? 'ingresos' : filter === 'EXPENSE' ? 'egresos' : 'movimientos'} registrados en esta sesión.</p>
                        </div>
                    ) : (
                        <div className="table-wrapper">
                            <table className="movimientos-table">
                                <thead>
                                    <tr>
                                        <th>Fecha/Hora</th>
                                        <th>Descripción</th>
                                        <th>Tipo</th>
                                        <th className="text-end">Monto</th>
                                        <th className="text-center">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredMovimientos.map((mov) => (
                                        <tr key={mov.id}>
                                            <td>
                                                {(() => {
                                                    const d = new Date(mov.fecha);
                                                    const options = {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        hour12: true
                                                    };

                                                    // Check for legacy "Midnight UTC" records
                                                    const isLegacyMidnight = d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0;

                                                    if (isLegacyMidnight) {
                                                        return d.toLocaleString('es-PE', { ...options, timeZone: 'UTC' });
                                                    } else {
                                                        return d.toLocaleString('es-PE', { ...options, timeZone: 'America/Lima' });
                                                    }
                                                })()}
                                            </td>
                                            <td className="movimiento-descripcion">
                                                {mov.descripcion}
                                            </td>
                                            <td>
                                                <span className={`badge-movimiento ${mov.tipo.toLowerCase()}`}>
                                                    {mov.tipo === 'DEPOSITO_BANCO' && 'DEPÓSITO'}
                                                    {mov.tipo === 'GASTO' && 'GASTO'}
                                                    {mov.tipo === 'GASTO_OPERATIVO' && 'EGRESO'}
                                                    {mov.tipo === 'INGRESO' && 'INGRESO'}
                                                    {mov.tipo === 'INGRESO_EXTRA' && 'INGRESO'}
                                                </span>
                                            </td>
                                            <td className={`text-end amount ${mov.es_salida ? 'salida' : 'entrada'}`}>
                                                {mov.es_salida ? '-' : '+'} S/ {mov.monto.toFixed(2)}
                                            </td>
                                            <td className="text-center">
                                                <div className="action-buttons-mov">
                                                    {mov.tipo === 'INGRESO' && (
                                                        <button
                                                            className="btn-action-mov edit"
                                                            onClick={() => handleEditMetodoPago(mov)}
                                                            title="Corregir Método de Pago"
                                                        >
                                                            <i className="fas fa-edit"></i>
                                                        </button>
                                                    )}
                                                    {mov.comprobante_id && (
                                                        <button
                                                            className="btn-action-mov print"
                                                            onClick={() => handlePrint(mov.comprobante_id)}
                                                            title="Ver Comprobante"
                                                        >
                                                            <i className="fas fa-print"></i>
                                                        </button>
                                                    )}
                                                    <button
                                                        className="btn-action-mov delete"
                                                        onClick={() => handleAnnul(mov)}
                                                        title="Anular"
                                                    >
                                                        <i className="fas fa-trash-alt"></i>
                                                    </button>
                                                </div>
                                            </td>


                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {!loading && filteredMovimientos.length > 0 && (
                    <div className="modal-total-summary">
                        <div className={`summary-item ${calculateTotal() >= 0 ? 'entrada' : 'salida'}`}>
                            <span className="summary-label">NETO EN SESIÓN:</span>
                            <span className="summary-value">S/ {calculateTotal().toFixed(2)}</span>
                        </div>
                    </div>
                )}


                <div className="modal-footer-movimientos">
                    <button className="btn btn-secondary" onClick={onClose}>
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CajaMovimientosModal;
